import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../services/apiClient';

const SavedProceduresContext = createContext(null);

export const SavedProceduresProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [savedIds, setSavedIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const loadSavedList = useCallback(async () => {
    if (!isAuthenticated) {
      setSavedIds(new Set());
      setLoaded(true);
      return;
    }
    try {
      const response = await apiClient.get('/api/SavedProcedures/list');
      setSavedIds(new Set(response.data || []));
    } catch {
      setSavedIds(new Set());
    } finally {
      setLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadSavedList();
  }, [loadSavedList]);

  const isSaved = useCallback((noticeNo) => savedIds.has(noticeNo), [savedIds]);

  const toggleSave = useCallback(async (noticeNo) => {
    if (!isAuthenticated) return false;

    const wasSaved = savedIds.has(noticeNo);
    try {
      if (wasSaved) {
        await apiClient.delete(`/api/SavedProcedures/${noticeNo}`);
        setSavedIds(prev => { const next = new Set(prev); next.delete(noticeNo); return next; });
      } else {
        await apiClient.post(`/api/SavedProcedures/${noticeNo}`);
        setSavedIds(prev => new Set(prev).add(noticeNo));
      }
      return true;
    } catch {
      return false;
    }
  }, [isAuthenticated, savedIds]);

  return (
    <SavedProceduresContext.Provider value={{ savedIds, isSaved, toggleSave, loaded, reload: loadSavedList }}>
      {children}
    </SavedProceduresContext.Provider>
  );
};

export const useSavedProcedures = () => {
  const ctx = useContext(SavedProceduresContext);
  if (!ctx) return { isSaved: () => false, toggleSave: async () => false, loaded: false, reload: () => {} };
  return ctx;
};
